import { Box, Button, forwardRef, MenuButton, RecursivePseudo, StyleProps } from '@chakra-ui/react';
import { PropsWithChildren } from 'react';

import { ChevronDownOutline } from '@bonusly/ds-icon';
import { rem } from '@bonusly/ds-utils';

import { useDropdownContext } from '../lib/context';
import { DropdownPlaceholder } from './dropdown-placeholder';

export interface DropdownInputProps extends StyleProps, RecursivePseudo<any>, PropsWithChildren {}

export const DropdownInput = forwardRef<DropdownInputProps, 'button'>(({ children, ...rest }, ref) => {
	const { id, isOpen, name, renderOption, value, valueToString } = useDropdownContext();

	const renderedChildren = (() => {
		if (value === undefined) {
			return <DropdownPlaceholder />;
		}

		if (children !== undefined) {
			return <>{children}</>;
		}

		if (renderOption !== undefined) {
			return renderOption(value);
		}

		if (valueToString !== undefined) {
			return valueToString(value);
		}

		return value;
	})();

	return (
		<>
			{typeof value === 'string' && <Box as="input" type="hidden" id={id} name={name} value={value} />}
			<MenuButton
				as={Button}
				bg="bg.surface"
				borderColor="border.default"
				borderStyle="solid"
				borderWidth="1px"
				fontWeight="normal"
				minH={rem(40)}
				minW={rem(192)}
				ref={ref}
				px={3}
				py={2}
				rightIcon={
					<ChevronDownOutline
						color="text.softest"
						size="xs"
						transitionDuration="normal"
						transitionProperty="transform"
						transform={isOpen ? 'rotate(180deg)' : undefined}
					/>
				}
				role="button"
				textAlign="left"
				_hover={{
					bg: 'bg.soft',
				}}
				_active={{
					borderColor: 'border.focus',
					outline: '1px solid',
					outlineOffset: '0',
					backgroundColor: 'transparent',
				}}
				sx={{
					'& > span:first-of-type': {
						overflow: 'hidden',
						textOverflow: 'ellipsis',
					},
				}}
				{...rest}
			>
				{renderedChildren}
			</MenuButton>
		</>
	);
});

DropdownInput.displayName = 'DropdownInput';
